import moment from 'moment-timezone';
import { displayDateFormat, displayDateTimeFormat, displayTimeFormat } from 'src/utils/dates';
import { ZoneChoice } from 'src/features/timezone/types/timezone.types';

export const UTC_ZONE = 'UTC';

export type CalendarDate = Temporal.PlainDate;

export type InstantFormat = 'dateTime' | 'date' | 'time' | 'monthDay';

const FORMAT_TOKENS: Record<InstantFormat, string> = {
  dateTime: displayDateTimeFormat,
  date: displayDateFormat,
  time: displayTimeFormat,
  monthDay: 'MMM D, YYYY'
};

export const localZoneId = (): string => Temporal.Now.timeZoneId();

export const resolveZone = (choice: ZoneChoice, storeZone?: string): string => {
  if (choice === 'store' && storeZone) return storeZone;
  if (choice === 'local') return localZoneId();
  return UTC_ZONE;
};

export const toInstant = (value: string): Temporal.Instant => {
  try {
    return Temporal.Instant.from(value);
  } catch {
    return Temporal.PlainDateTime.from(value).toZonedDateTime(UTC_ZONE).toInstant();
  }
};

export const toPlainDate = (date: Date): Temporal.PlainDate =>
  new Temporal.PlainDate(date.getFullYear(), date.getMonth() + 1, date.getDate());

export const parsePlainDate = (value: string): Temporal.PlainDate | null => {
  try {
    return Temporal.PlainDate.from(value);
  } catch {
    return null;
  }
};

export const formatPlainDate = (date: Temporal.PlainDate): string => date.toString();

export const todayIn = (zone: string): CalendarDate => Temporal.Now.plainDateISO(zone);

export const toPickerDate = (date: CalendarDate): Date =>
  new Date(date.year, date.month - 1, date.day);

export const calendarRangeToInstants = (
  start: Temporal.PlainDate,
  end: Temporal.PlainDate,
  zone: string
): { startISO: string; endISO: string } => {
  const startInstant = start.toZonedDateTime(zone).toInstant();
  const endInstant = end
    .add({ days: 1 })
    .toZonedDateTime(zone)
    .toInstant()
    .subtract({ milliseconds: 1 });
  return { startISO: startInstant.toString(), endISO: endInstant.toString() };
};

export const wallTimeToInstant = (
  value: string,
  zone: string,
  snap?: 'startOfMinute' | 'endOfMinute'
): string => {
  let dt = Temporal.PlainDateTime.from(value.replace(' ', 'T'));
  if (snap === 'startOfMinute') dt = dt.with({ second: 0, millisecond: 0 });
  if (snap === 'endOfMinute') dt = dt.with({ second: 59, millisecond: 999 });
  return dt.toZonedDateTime(zone).toInstant().toString();
};

export const instantToWallTime = (value: string, zone: string): string =>
  toInstant(value)
    .toZonedDateTimeISO(zone)
    .toPlainDateTime()
    .toString({ smallestUnit: 'minute' });

export const formatInstant = (value: string, zone: string, format: InstantFormat = 'dateTime'): string =>
  moment.tz(toInstant(value).epochMilliseconds, zone).format(FORMAT_TOKENS[format]);

export const zoneAbbreviation = (zone: string, value?: string): string => {
  const epochMs = value ? toInstant(value).epochMilliseconds : Date.now();
  return moment.tz(epochMs, zone).format('z');
};
